import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { debounce, filter, forEach, isEmpty, map } from 'lodash';
import { React, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as PlayIcon } from '../../../../../assets/svg/play-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../../assets/svg/right-from-bracket-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DAMAGE_REQUEST_STATUS,
  DATE_FORMATS,
  LIBRARY_DAMAGE_REQUEST_PENDING_COMMENT_REASONS,
  LIBRARY_DAMAGE_REQUEST_RESOLVED_COMMENT_REASONS,
  LIMIT,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  REQUEST_STATUS,
  REQUEST_TYPES,
  ROUTES,
  SORT,
  STATUS_LIBRARY_DAMAGE_FILTER,
  USER_ROLES,
} from '../../../../../common/constants';
import { formatDate, hasPermission } from '../../../../../common/utils';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../components/SearchComponent';
import CommentModal from '../../../../../libraryModules/raiseRequest/components/CommentModal';
import ToyVideoModal from '../../../../../libraryModules/raiseRequest/components/ToyVideoModal';
import ViewCommentsModal from '../../../../../libraryModules/raiseRequest/components/ViewCommentsModal';
import ContentMasterModal from '../../../../orders/modules/all/components/ContentMasterModal';
import { UPDATE_REQUEST_ADMIN } from '../../../graphql/Mutations';
import { USERS } from '../../../graphql/Queries';
import {
  LIBRARIES,
  LIBRARY_DAMAGE_REQUESTS_ADMIN,
  TOYS,
} from '../graphql/Queries';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialDamageFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialDamageSort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

const roleFilter = [USER_ROLES?.DELIVERY?.key];
let filterScrollDebounceJob = null;

const DamageTable = ({ exportFilter, setExportFilter }) => {
  const {
    state: {
      permissions,
      commonPermissions: {
        isUserPermission,
        isDeliveryPartnerPermission,
        isLibraryPermission,
        isToyPermission,
      },
    },
  } = useContext(AppContext);
  const history = useHistory();
  const [form] = Form.useForm();
  const [damageList, setDamageList] = useState([]);
  const [damageSearchTerm, setDamageSearchTerm] = useState('');
  const [isEmptyDamageList, setIsEmptyDamageList] = useState(false);
  const [damage, setDamage] = useState({});
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isArchivePrompts, setIsArchivePrompts] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [isCommentLoading, setIsCommentLoading] = useState(false);
  const [isCommentPrompts, setIsCommentPrompts] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isViewCommentPrompts, setIsViewCommentPrompts] = useState(false);

  const [isDamageLoading, setIsDamageLoading] = useState(true);
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [isPendingComment, setIsPendingComment] = useState(false);

  const [requestsAdmin] = useLazyQuery(LIBRARY_DAMAGE_REQUESTS_ADMIN, {
    onCompleted: (response) => {
      setDamageList([...response?.requestsAdmin?.data]);
      if (
        response?.requestsAdmin?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyDamageList(true);
      } else {
        setIsEmptyDamageList(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.requestsAdmin?.count,
      };
      setPaginationProp(pagination);
      setIsDamageLoading(false);
    },
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const [libraries, { loading: isLibrariesLoading }] = useLazyQuery(LIBRARIES, {
    onCompleted: (response) => {
      if (response?.libraries?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.libraries?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.libraries?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [users, { loading: isUsersLoading }] = useLazyQuery(USERS, {
    onCompleted: (response) => {
      if (response?.users?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.users?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.users?.data, (item) =>
        optionsCopy?.push({
          label: `${item?.firstName} ${item?.lastName}`,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [toys, { loading: isToysLoading }] = useLazyQuery(TOYS, {
    onCompleted: (response) => {
      if (response?.toys?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.toys?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.toys?.data, (item) =>
        optionsCopy?.push({ label: item?.name, key: item?.id }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [updateRequestAdmin] = useMutation(UPDATE_REQUEST_ADMIN, {
    onError: () => {},
  });

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.REQUESTS_LIBRARY_DAMAGES,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsDamageLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialDamageFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: damageSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          deliveryPartnerIds: finalWhereFilters?.deliveryPartnerName,
          productIds: finalWhereFilters?.productName,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialDamageSort,
      },
    });
    setExportFilter({ ...exportFilter, isArchived: isArchiveFilter });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsDamageLoading(true);
    setSortedInfo(sorter);
    requestsAdmin({
      variables: {
        filter: {
          ...initialDamageFilter,
          skip,
          limit: pagination?.pageSize,
          search: damageSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          deliveryPartnerIds: finalWhereFilters?.deliveryPartnerName,
          productIds: finalWhereFilters?.productName,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialDamageSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setDamageSearchTerm(trimValue);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsDamageLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialDamageFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          deliveryPartnerIds: finalWhereFilters?.deliveryPartnerName,
          productIds: finalWhereFilters?.productName,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialDamageSort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleArchive = async (id, isArchived) => {
    setIsArchiveLoading(true);
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id,
        },
        data: {
          isArchived: !isArchived,
        },
      },
    });
    if (response?.data) {
      setIsArchiveLoading(false);
      setIsDamageLoading(true);
      setIsArchivePrompts(false);
      requestsAdmin({
        variables: {
          filter: {
            ...initialDamageFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: damageSearchTerm,
            isArchived: isArchiveFilter,
            type: REQUEST_TYPES?.LIBRARY_DAMAGE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            deliveryPartnerIds: finalWhereFilters?.deliveryPartnerName,
            productIds: finalWhereFilters?.productName,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialDamageSort,
        },
      });
    }
    setIsArchiveLoading(false);
  };

  const handleAddComment = async (values) => {
    setIsCommentLoading(true);
    const commentPayload = {
      updatedAt: dayjs()?.format(),
      reason: values?.reason,
      comment: values?.comment?.trim() || '',
    };
    const payload = isPendingComment
      ? { pendingComment: commentPayload }
      : { resolveComment: commentPayload };
    const status = isPendingComment
      ? REQUEST_STATUS?.PENDING?.value
      : REQUEST_STATUS?.RESOLVED?.value;
    const response = await updateRequestAdmin({
      variables: {
        where: {
          id: damage?.id,
        },
        data: {
          libraryDamage: {
            ...payload,
          },
          status,
        },
      },
    });

    if (response?.data) {
      setIsDamageLoading(true);
      setIsCommentPrompts(false);
      form?.resetFields();
      requestsAdmin({
        variables: {
          filter: {
            ...initialDamageFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            search: damageSearchTerm,
            type: REQUEST_TYPES?.LIBRARY_DAMAGE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            deliveryPartnerIds: finalWhereFilters?.deliveryPartnerName,
            productIds: finalWhereFilters?.productName,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialDamageSort,
        },
      });
    }
    setIsCommentLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsDamageLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialDamageFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: damageSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          libraryIds: whereFilter?.name,
          deliveryPartnerIds: whereFilter?.deliveryPartnerName,
          productIds: whereFilter?.productName,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialDamageSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsDamageLoading(true);
    requestsAdmin({
      variables: {
        filter: {
          ...initialDamageFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: damageSearchTerm,
          isArchived: isArchiveFilter,
          type: REQUEST_TYPES?.LIBRARY_DAMAGE,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          libraryIds: whereFiltersCopy?.name,
          deliveryPartnerIds: whereFiltersCopy?.deliveryPartnerName,
          productIds: whereFiltersCopy?.productName,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialDamageSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS_LIBRARY_DAMAGE_FILTER, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'name':
          libraries({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'productName':
          toys({
            variables: {
              filter: initialTableFilter,
              sort: initialTableSort,
            },
          });
          break;

        case 'deliveryPartnerName':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: roleFilter,
              },
              sort: initialTableSort,
            },
          });
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isLibrariesLoading &&
      !isUsersLoading &&
      !isToysLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'name':
              libraries({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'productName':
              toys({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'deliveryPartnerName':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: roleFilter,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'name':
        libraries({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'productName':
        toys({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'deliveryPartnerName':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: roleFilter,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const findLabelByValue = (value, isPendingRequest) => {
    const item = isPendingRequest
      ? LIBRARY_DAMAGE_REQUEST_PENDING_COMMENT_REASONS.find(
          (reason) => reason.value === value,
        )
      : LIBRARY_DAMAGE_REQUEST_RESOLVED_COMMENT_REASONS.find(
          (reason) => reason.value === value,
        );
    return item ? item.label : value;
  };

  const columns = [
    {
      title: 'LIBRARY NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      onCell: (record) => ({
        onClick: () =>
          isLibraryPermission &&
          record?.library?.id &&
          history?.push(
            `${ROUTES?.MASTER_LISTS}${ROUTES?.LIBRARY}${ROUTES?.BASIC_DETAILS}/view/${record?.library?.id}/${record?.library?.libraryNumber}`,
          ),
        className: `${
          isLibraryPermission && record?.library?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          <div className="table-data-with-id">{record?.library?.name}</div>
          <div className="font-size-12">({record?.library?.libraryNumber})</div>
        </>
      ),
      ...filterPopup('name'),
    },
    {
      title: 'LIBRARIAN NAME',
      dataIndex: 'librarianName',
      key: 'librarianName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isUserPermission &&
          record?.createdBy?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}${ROUTES?.BASIC_DETAILS}/view/${record?.createdBy?.id}/${record?.createdBy?.userNumber}`,
          ),
        className: `${
          isUserPermission && record?.createdBy?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.createdBy;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'USER NAME',
      dataIndex: 'userName',
      key: 'userName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isUserPermission &&
          record?.damager?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}${ROUTES?.BASIC_DETAILS}/view/${record?.damager?.id}/${record?.damager?.userNumber}`,
          ),
        className: `${
          isUserPermission && record?.damager?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.damager;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'REQUEST ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      width: 170,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.requestNumber,
    },
    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isDeliveryPartnerPermission &&
          record?.returnOrderItemDetails?.[0]?.returnOrder?.deliveryPartner
            ?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.DELIVERY_PARTNERS}/view/${record?.returnOrderItemDetails?.[0]?.returnOrder?.deliveryPartner?.id}`,
          ),
        className: `${
          isDeliveryPartnerPermission &&
          record?.returnOrderItemDetails?.[0]?.returnOrder?.deliveryPartner &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const deliveryPartnerData =
          record?.returnOrderItemDetails?.[0]?.returnOrder?.deliveryPartner;
        return (
          <>
            {deliveryPartnerData?.firstName || deliveryPartnerData?.lastName ? (
              <div className="table-data-with-id">{`${
                deliveryPartnerData?.firstName
                  ? deliveryPartnerData?.firstName
                  : ''
              } ${
                deliveryPartnerData?.lastName
                  ? deliveryPartnerData?.lastName
                  : ''
              }`}</div>
            ) : (
              '-'
            )}
            {deliveryPartnerData?.userNumber ? (
              <div className="font-size-12">
                ({deliveryPartnerData?.userNumber})
              </div>
            ) : (
              ''
            )}
          </>
        );
      },
      ...filterPopup('deliveryPartnerName'),
    },
    {
      title: 'PRODUCT NAME',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isToyPermission &&
          record?.returnOrderItemDetails?.[0]?.libraryInventory?.toy?.id &&
          history?.push(
            `${ROUTES?.MASTER_LISTS}${ROUTES?.TOY}/view/${record?.returnOrderItemDetails?.[0]?.libraryInventory?.toy?.id}`,
          ),
        className: `${
          isToyPermission &&
          record?.returnOrderItemDetails?.[0]?.libraryInventory?.toy?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const toyData =
          record?.returnOrderItemDetails?.[0]?.libraryInventory?.toy;
        return (
          <>
            <div className="table-data-with-id">{toyData?.name || '-'}</div>
            {toyData?.toyNumber && (
              <div className="font-size-12">({toyData?.toyNumber})</div>
            )}
          </>
        );
      },
      ...filterPopup('productName'),
    },
    {
      title: 'REQUEST DATE',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'TOTAL ORDERED',
      dataIndex: 'orderCount',
      key: 'orderCount',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.metaData?.damageItemFullfilledCount ?? '-',
    },
    {
      title: 'IMAGES',
      dataIndex: 'images',
      key: 'images',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <span>
          {record?.images?.length > 0 ? (
            <>
              <Image
                preview={{
                  visible: false,
                }}
                width={20}
                height={20}
                src={record?.images?.[0]?.url}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(record?.images);
                }}
                className="pointer"
              />
            </>
          ) : (
            <span>No Images</span>
          )}
          {record?.images?.length > 1 ? ` + ${record?.images?.length - 1}` : ''}
        </span>
      ),
    },
    {
      title: 'VIDEO',
      dataIndex: 'video',
      key: 'video',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <span>
          {record?.videoUrl ? (
            <Tooltip title="Play" placement="top" zIndex={4}>
              <PlayIcon
                onClick={() => {
                  setDamage(record);
                  setIsVideoModalOpen(true);
                }}
                className="mr-16 pointer svg-icon"
              />
            </Tooltip>
          ) : (
            '-'
          )}
        </span>
      ),
    },
    {
      title: 'DAMAGE DESCRIPTION',
      dataIndex: 'damageDescription',
      key: 'damageDescription',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      render: (_, record) => (
        <Tooltip
          title={
            record?.description ? record?.description : 'No Damage Description'
          }
          placement="topLeft"
          trigger="click"
          className="pointer"
        >
          {record?.description || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'PENDING COMMENT',
      dataIndex: 'pendingComment',
      key: 'pendingComment',
      ellipsis: true,
      width: 150,
      render: (_, record) =>
        [
          REQUEST_STATUS?.RESOLVED?.value,
          REQUEST_STATUS?.APPROVED?.value,
          REQUEST_STATUS?.PENDING?.value,
          REQUEST_STATUS?.REJECTED?.value,
        ]?.includes(record?.status) && record?.metaData?.pendingComment ? (
          <>
            <div className="action-button">
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  form?.setFieldsValue({
                    comment: record?.metaData?.pendingComment?.comment,
                    reason: findLabelByValue(
                      record?.metaData?.pendingComment?.reason,
                      true,
                    ),
                  });
                  setIsPendingComment(true);
                  setDamage(record);
                  setIsViewCommentPrompts(true);
                }}
              />
            </div>
          </>
        ) : (
          '-'
        ),
    },
    {
      title: 'RESOLVING COMMENT',
      dataIndex: 'resolvingComment',
      key: 'resolvingComment',
      ellipsis: true,
      width: 150,
      render: (_, record) =>
        ([
          REQUEST_STATUS?.RESOLVED?.value,
          REQUEST_STATUS?.APPROVED?.value,
          REQUEST_STATUS?.PENDING?.value,
          REQUEST_STATUS?.REJECTED?.value,
        ]?.includes(record?.status) &&
          record?.metaData?.resolveComment) ||
        record?.metaData?.comment ? (
          <>
            <div className="action-button">
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  form?.setFieldsValue({
                    comment:
                      record?.metaData?.resolveComment?.comment ||
                      record?.metaData?.comment,
                    reason: findLabelByValue(
                      record?.metaData?.resolveComment?.reason,
                      false,
                    ),
                  });
                  setDamage(record);
                  setIsPendingComment(false);
                  setIsViewCommentPrompts(true);
                }}
              />
            </div>
          </>
        ) : (
          '-'
        ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={DAMAGE_REQUEST_STATUS?.[record?.status]?.color}
          text={DAMAGE_REQUEST_STATUS?.[record?.status]?.text}
        />
      ),
      ...filterPopup('status', false),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      ellipsis: true,
      width: 150,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      render: (_, record) => (
        <>
          <div className="action-button">
            {isArchiveFilter ? (
              <Tooltip title="Unarchive" placement="top" zIndex={4}>
                <UnarchiveIcon
                  onClick={() => {
                    setIsArchivePrompts(true);
                    setDamage(record);
                  }}
                  className="mr-16 pointer svg-icon"
                />
              </Tooltip>
            ) : (
              <>
                <div className="mr-16">
                  <Button
                    type="primary"
                    size="small"
                    className="table-action-primary-btn"
                    onClick={() => {
                      setDamage(record);
                      setIsContentMasterModalOpen(true);
                    }}
                  >
                    View Content Logs
                  </Button>
                </div>
                <div className="mr-16">
                  <Button
                    type="primary"
                    size="small"
                    className="table-action-primary-btn"
                    onClick={() => {
                      setIsPendingComment(false);
                      setIsCommentPrompts(true);
                      setDamage(record);
                    }}
                    disabled={[
                      REQUEST_STATUS?.APPROVED?.value,
                      REQUEST_STATUS?.REJECTED?.value,
                      REQUEST_STATUS?.RESOLVED?.value,
                    ]?.includes(record?.status)}
                  >
                    Resolve
                  </Button>
                </div>
                <Button
                  type="primary"
                  size="small"
                  className="table-action-primary-btn"
                  onClick={() => {
                    setIsPendingComment(true);
                    setIsCommentPrompts(true);
                    setDamage(record);
                  }}
                  disabled={
                    [
                      REQUEST_STATUS?.APPROVED?.value,
                      REQUEST_STATUS?.REJECTED?.value,
                      REQUEST_STATUS?.RESOLVED?.value,
                    ]?.includes(record?.status) ||
                    record?.metaData?.pendingComment
                  }
                >
                  Add Comment
                </Button>
                <Tooltip title="Archive" placement="top" zIndex={4}>
                  <ArchiveIcon
                    onClick={() => {
                      setIsArchivePrompts(true);
                      setDamage(record);
                    }}
                    className="mr-16 pointer svg-icon ml-16"
                  />
                </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ]?.filter((item) => {
    if (item?.dataIndex === 'actions' && !hasUpdatePermission) {
      return item?.hidden;
    }
    return !item?.hidden;
  });

  const locale = {
    emptyText: isEmptyDamageList ? '' : <span />,
  };

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setDamage({});
  };

  return (
    <>
      <ContentMasterModal
        isModalOpen={isContentMasterModalOpen}
        contentHistory={damage?.returnOrderItemDetails?.[0]?.contentHistory}
        handleCancel={handleCancel}
        isReturn
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompts}
        onOk={() => handleArchive(damage?.id, damage?.isArchived)}
        onCancel={() => setIsArchivePrompts(false)}
        okButtonProps={{ loading: isArchiveLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!damage?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this request?
      </Modal>
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <ToyVideoModal
        title={`Video - ${damage?.createdBy?.userNumber}`}
        isModalOpen={isVideoModalOpen}
        setIsModalOpen={setIsVideoModalOpen}
        videoUrl={damage?.videoUrl}
      />
      <CommentModal
        form={form}
        isModalOpen={isCommentPrompts}
        setIsModalOpen={setIsCommentPrompts}
        isBtnLoading={isCommentLoading}
        onFinish={handleAddComment}
        isPendingComment={isPendingComment}
      />
      <ViewCommentsModal
        isModalOpen={isViewCommentPrompts}
        setIsModalOpen={setIsViewCommentPrompts}
        adminComment={damage?.metaData?.comment}
        libraryComments={damage?.metaData?.actionsByLibrary}
        status={damage?.status}
        metadata={damage?.metaData}
        isPendingComment={isPendingComment}
        form={form}
        onFinish={handleAddComment}
        damage={damage}
      />
      <div className="d-flex justify-end mb-16 flex-wrap">
        {hasUpdatePermission && (
          <div className="mr-16 d-flex align-center">
            <h4 className="m-0 mr-8">Only Archived</h4>{' '}
            <Switch
              size="small"
              onChange={(checked) => setIsArchiveFilter(checked)}
            />
          </div>
        )}
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        locale={locale}
        columns={columns}
        data={damageList || []}
        loading={isDamageLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default DamageTable;
